import React from 'react';
import { Table, Card, Tag, Descriptions, Button, Space, Typography, Form, Input, Select, message } from 'antd';
import { Link } from 'react-router-dom';
import { CardPayload } from '../../services/agentApi';

const { Text } = Typography;
const { TextArea } = Input;

interface MessageCardsProps {
  cards: CardPayload[];
}

export const MessageCards: React.FC<MessageCardsProps> = ({ cards }) => {
  if (!cards.length) return null;

  return (
    <div className="agent-message-cards">
      {cards.map((card, idx) => (
        <Card key={idx} size="small" title={card.title} className="agent-card" style={{ marginTop: 10 }}>
          {renderCardContent(card)}
          {card.links && card.links.length > 0 && (
            <Space wrap style={{ marginTop: 12 }}>
              {card.links.map((link, i) => (
                <Link key={i} to={link.path}>
                  <Button type="link" size="small">{link.label}</Button>
                </Link>
              ))}
            </Space>
          )}
        </Card>
      ))}
    </div>
  );
};

function renderCardContent(card: CardPayload): React.ReactNode {
  const data = card.data;

  switch (card.type) {
    case 'protocol_list':
    case 'project_list':
    case 'device_list':
    case 'settlement_list':
    case 'yingshi_status':
      return renderTable(card.type, data);
    case 'device_status':
    case 'device_detail':
      return renderDeviceDetail(data);
    case 'device_form':
      return renderDeviceForm(data, card);
    case 'docking_logs':
      return renderDockingLogs(data);
    case 'actions_list':
      return renderActions(data);
    case 'form_schema':
      return renderFormSchema(data);
    case 'confirm_write':
      return (
        <div>
          <Tag color="success">操作成功</Tag>
          {renderJsonBlock((data as Record<string, unknown>)?.result || data)}
        </div>
      );
    default:
      return renderJsonBlock(data);
  }
}

function renderTable(type: string, data: unknown): React.ReactNode {
  const items = Array.isArray(data) ? data : (data as { items?: unknown[] })?.items;
  if (!Array.isArray(items) || items.length === 0) {
    return <Text type="secondary">暂无数据</Text>;
  }

  const columnsMap: Record<string, Array<{ title: string; dataIndex: string; key: string; render?: (v: unknown) => React.ReactNode }>> = {
    protocol_list: [
      { title: 'ID', dataIndex: 'id', key: 'id' },
      { title: '协议代码', dataIndex: 'name', key: 'name' },
      { title: '协议名称', dataIndex: 'title', key: 'title' },
      { title: '状态', dataIndex: 'status', key: 'status' },
    ],
    project_list: [
      { title: 'ID', dataIndex: 'id', key: 'id' },
      { title: '项目名称', dataIndex: 'name', key: 'name' },
      { title: '项目代码', dataIndex: 'projectCode', key: 'projectCode' },
      { title: '区域', dataIndex: 'region', key: 'region' },
      { title: '状态', dataIndex: 'status', key: 'status' },
    ],
    device_list: [
      { title: 'ID', dataIndex: 'id', key: 'id' },
      { title: '设备编号', dataIndex: 'deviceCode', key: 'deviceCode' },
      { title: '设备名称', dataIndex: 'deviceName', key: 'deviceName' },
      { title: '类型', dataIndex: 'deviceTypeName', key: 'deviceTypeName' },
      { title: '结算', dataIndex: 'isPay', key: 'isPay' },
    ],
    settlement_list: [
      { title: '设备编号', dataIndex: 'deviceCode', key: 'deviceCode' },
      { title: '设备名称', dataIndex: 'deviceName', key: 'deviceName' },
      { title: '类型', dataIndex: 'deviceType', key: 'deviceType' },
      { title: '结算状态', dataIndex: 'isPay', key: 'isPay' },
    ],
    yingshi_status: [
      { title: '设备序列号', dataIndex: 'deviceSerial', key: 'deviceSerial' },
      { title: '设备名称', dataIndex: 'deviceName', key: 'deviceName' },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        render: (v: unknown) => (
          <Tag color={v === '在线' ? 'green' : 'default'}>{String(v)}</Tag>
        ),
      },
    ],
  };

  const columns = columnsMap[type] || Object.keys(items[0] as object).map((k) => ({
    title: k,
    dataIndex: k,
    key: k,
  }));

  return (
    <Table
      size="small"
      dataSource={items as Record<string, unknown>[]}
      columns={columns}
      rowKey={(r) => String((r as Record<string, unknown>).id || (r as Record<string, unknown>).deviceCode || Math.random())}
      pagination={items.length > 10 ? { pageSize: 10 } : false}
      scroll={{ x: true }}
    />
  );
}

function renderDockingLogs(data: unknown): React.ReactNode {
  const d = data as { summary?: Record<string, unknown>; logs?: Array<Record<string, unknown>> };
  if (!d?.summary) return renderJsonBlock(data);

  return (
    <div>
      <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="最近状态">{String(d.summary.latestStatus)}</Descriptions.Item>
        <Descriptions.Item label="最近时间">{String(d.summary.latestTime || '-')}</Descriptions.Item>
        <Descriptions.Item label="成功">{String(d.summary.successCount)}</Descriptions.Item>
        <Descriptions.Item label="失败">{String(d.summary.failCount)}</Descriptions.Item>
      </Descriptions>
      {d.logs && d.logs.length > 0 && (
        <Table
          size="small"
          dataSource={d.logs}
          rowKey={(r) => String(r.id || r.logTime)}
          pagination={false}
          columns={[
            { title: '时间', dataIndex: 'logTime', key: 'logTime' },
            { title: '类型', dataIndex: 'type', key: 'type' },
            {
              title: '状态',
              dataIndex: 'statusText',
              key: 'statusText',
              render: (v: unknown) => (
                <Tag color={v === '成功' ? 'green' : 'red'}>{String(v)}</Tag>
              ),
            },
            { title: '内容', dataIndex: 'content', key: 'content', ellipsis: true },
          ]}
        />
      )}
    </div>
  );
}

function renderActions(data: unknown): React.ReactNode {
  const items = Array.isArray(data) ? data : [];
  if (!items.length) return <Text type="secondary">暂无对接操作</Text>;
  return (
    <Table
      size="small"
      dataSource={items as Record<string, unknown>[]}
      rowKey="id"
      pagination={false}
      columns={[
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: '操作名称', dataIndex: 'title', key: 'title' },
        { title: '动作', dataIndex: 'action', key: 'action' },
        { title: '设备类型', dataIndex: 'deviceType', key: 'deviceType' },
        { title: '方法', dataIndex: 'method', key: 'method' },
      ]}
    />
  );
}

function renderFormSchema(data: unknown): React.ReactNode {
  const d = data as { fields?: Array<Record<string, unknown>>; requiredFields?: string[] };
  if (!d?.fields) return renderJsonBlock(data);

  return (
    <Table
      size="small"
      dataSource={d.fields}
      rowKey="key"
      pagination={false}
      columns={[
        { title: '字段', dataIndex: 'key', key: 'key' },
        { title: '标签', dataIndex: 'label', key: 'label' },
        { title: '类型', dataIndex: 'type', key: 'type' },
        {
          title: '必填',
          dataIndex: 'required',
          key: 'required',
          render: (v: unknown) => (v ? <Tag color="red">是</Tag> : <Tag>否</Tag>),
        },
      ]}
    />
  );
}

function renderJsonBlock(data: unknown): React.ReactNode {
  if (!data) return <Text type="secondary">无数据</Text>;
  return (
    <pre className="agent-json-block">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function renderDeviceDetail(data: unknown): React.ReactNode {
  const device = data as Record<string, unknown>;
  if (!device) return <Text type="secondary">无设备数据</Text>;

  const exData = device.exData as Record<string, unknown> | undefined;
  const hasExData = exData && typeof exData === 'object';

  return (
    <Descriptions size="small" column={2} bordered>
      <Descriptions.Item label="设备ID">{String(device.deviceCode || device.id || '-')}</Descriptions.Item>
      <Descriptions.Item label="设备名称">{String(device.deviceName || '-')}</Descriptions.Item>
      <Descriptions.Item label="设备类型">{String(device.deviceTypeName || device.deviceType || '-')}</Descriptions.Item>
      <Descriptions.Item label="项目">{String(device.projectName || '-')}</Descriptions.Item>
      <Descriptions.Item label="区域">{String(device.area || '-')}</Descriptions.Item>
      <Descriptions.Item label="状态">
        <Tag color={device.isPay ? 'green' : 'red'}>
          {device.isPay ? '已支付' : '未支付'}
        </Tag>
      </Descriptions.Item>
      {hasExData && (
        <>
          <Descriptions.Item label="驾驶员">{String(exData.driverName || '-')}</Descriptions.Item>
          <Descriptions.Item label="身份证">{String(exData.idcardNumber || '-')}</Descriptions.Item>
        </>
      )}
    </Descriptions>
  );
}

function renderDeviceForm(data: unknown, card: CardPayload): React.ReactNode {
  const formData = data as { fields?: Array<{ key: string; label: string; type: string; options?: Array<{ label: string; value: string }>; required?: boolean }>; values?: Record<string, unknown> };
  
  if (!formData?.fields) return <Text type="secondary">无表单数据</Text>;

  const [form] = Form.useForm();

  const handleSubmit = async (values: Record<string, unknown>) => {
    message.loading({ content: '提交中...', key: 'deviceForm' });
    // Simulate API call - in real implementation, this would call the actual API
    setTimeout(() => {
      message.success({ content: '提交成功', key: 'deviceForm' });
    }, 1000);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={formData.values}
      size="small"
    >
      {formData.fields.map((field) => (
        <Form.Item
          key={field.key}
          name={field.key}
          label={field.label}
          rules={field.required ? [{ required: true, message: `请输入${field.label}` }] : undefined}
        >
          {field.type === 'textarea' ? (
            <TextArea rows={3} placeholder={`请输入${field.label}`} />
          ) : field.type === 'select' ? (
            <Select placeholder={`请选择${field.label}`}>
              {field.options?.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          ) : (
            <Input placeholder={`请输入${field.label}`} />
          )}
        </Form.Item>
      ))}
      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" size="small">
            提交
          </Button>
          <Button size="small" onClick={() => form.resetFields()}>
            重置
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}

export default MessageCards;
